import ReactMarkdown from "react-markdown";
import { Link } from "react-router-dom";
import { ShieldCheck, ArrowRight } from "lucide-react";
import SEO, { orgSchema, breadcrumbSchema } from "@/components/SEO";

const PRIVACY_POLICY = `**Versión 3.1 — vigente desde el 22 de febrero de 2026.** Esta política cumple con los 12 requisitos del **Art. 14 ter** de la Ley N° 21.719 de Protección de Datos Personales de Chile.

## 1. Política de tratamiento (versión vigente)

Versión 3.1 — vigente desde el 22 de febrero de 2026. Reemplaza la versión 3.0 (también del 22 de febrero de 2026). Cambio material: se añadió la declaración del programa opcional de **3 emails educativos post-scan** (ver sección 14). Cambios materiales se notifican por email a usuarios registrados.

## 2. Responsable del tratamiento

Ley de Datos Chile (en adelante, "Ley de Datos Chile" o "el Responsable") es responsable del tratamiento de los datos personales recolectados a través de https://leydedatoschile.com y servicios asociados.

- **Sitio web:** https://leydedatoschile.com
- **Encargado de prevención / Delegado de Protección de Datos (DPO):** el mismo responsable ejerce ambas funciones para esta etapa inicial.
- **Jurisdicción:** Chile

> **Aclaración legal:** la Ley 21.719 distingue dos figuras: el **Delegado de Protección de Datos (DPO, Art. 50)** —responsable del cumplimiento de protección de datos— y el **Encargado de Prevención (Art. 49)** del modelo de prevención de delitos. En Ley de Datos Chile ambas funciones recaen en el responsable mientras crecemos.

## 3. Canal de notificaciones y contacto

- **Email para temas de privacidad:** privacidad@leydedatoschile.com
- **Formulario de contacto:** disponible en https://leydedatoschile.com
- **Domicilio postal:** Chile (puedes solicitar la dirección física vía email para notificaciones formales).

## 4. Categorías de datos, titulares, destinatarios, finalidades y base de legitimidad

**Categorías de datos:** email, URL del sitio, datos de tu empresa para el generador (RUT, razón social, rubro), registro de consentimiento (incluido consentimiento separado para emails educativos opcionales y token de baja), datos técnicos de navegación (IP anonimizada, user-agent).

**Universo de titulares:** personas naturales que representan PYMEs chilenas y visitan el sitio.

**Destinatarios (encargados del tratamiento):**
- **MongoDB Atlas** — almacenamiento.
- **Anthropic (Claude)** — análisis y generación de documentos. Datos no se usan para entrenar modelos.
- **Resend** — emails transaccionales.
- **Mercado Pago** — procesamiento de pagos.
- **PostHog (PostHog Inc., EE.UU.)** — analítica y session recording de uso del sitio. **Solo si otorgaste consentimiento** en el banner de cookies. Inputs de formularios enmascarados.
- **Google Analytics 4 (Google LLC, EE.UU.)** — medición de embudo de conversión con IP anonimizada. **Solo si otorgaste consentimiento** en el banner de cookies.
- **Proveedor de hosting** — infraestructura.

**Finalidades:** entrega del escáner, generación de documentos legales, comunicaciones transaccionales (reporte por email, recibos de pago, notificaciones de cuenta), alertas regulatorias y — **solo si lo aceptás expresamente** — un programa educativo opcional de 3 emails sobre la Ley 21.719 (ver sección 14).

**Base de legitimidad:** consentimiento explícito (Art. 12), ejecución del contrato, obligación legal (registros fiscales). **No tratamos** datos sensibles bajo "interés legítimo" sin consentimiento previo.

## 5. Política y medidas de seguridad

Implementamos medidas alineadas con el **Decreto N° 7 de 2023 del Ministerio Secretaría General de la Presidencia** (Norma Técnica de Seguridad de la Información):

- HTTPS/TLS 1.2+ en tránsito, cifrado en reposo (MongoDB).
- Contraseñas con bcrypt (cost factor 12), tokens JWT de expiración corta.
- Control de accesos por rol, logs de auditoría, monitoreo continuo.
- Notificación de brechas a la APDP y a titulares afectados en máximo **72 horas** (Art. 23).

## 6. Derechos del titular (ARCO + Portabilidad)

Tienes derecho en cualquier momento a:

- **Acceder** a los datos personales que tenemos sobre ti.
- **Rectificar** datos inexactos o incompletos.
- **Cancelar (suprimir)** tus datos cuando ya no sean necesarios.
- **Oponerte** a tratamientos específicos.
- **Portar** tus datos a otro proveedor en formato estructurado.

Para ejercer estos derechos, escribe a **privacidad@leydedatoschile.com** con asunto "Derechos ARCO". Respondemos en máximo **30 días corridos**, según exige la Ley 21.719.

## 7. Derecho a recurrir ante la Agencia (APDP)

Si rechazamos o no respondemos oportunamente tu solicitud, tienes derecho a recurrir ante la **Agencia de Protección de Datos Personales (APDP)** presentando un reclamo formal. La APDP es la autoridad competente para fiscalizar el cumplimiento de la Ley 21.719.

## 8. Transferencias internacionales

Algunos encargados procesan datos fuera de Chile (EE.UU. para Resend, Anthropic, PostHog y Google Analytics; EE.UU./Brasil para Mercado Pago; EE.UU. para el hosting).

Estas transferencias se realizan bajo las **Cláusulas Contractuales Modelo para Transferencias Internacionales aprobadas por el Ministerio de Economía, Fomento y Turismo (RAEX 2025-03731, diciembre 2025)**, que cumplen con el estándar exigido por la Ley 21.719.

## 9. Plazos de conservación

| Categoría de dato | Plazo | Condiciones |
|---|---|---|
| Email y onboarding | Mientras tengas cuenta activa + 1 año | Tras la última actividad. Se elimina automáticamente. |
| Documentos generados | Indefinido | Mientras tengas cuenta. Puedes solicitar borrado vía ARCO. |
| Cola de emails educativos (drip) | Hasta 7 días desde el scan | Se borra automáticamente al completarse o tras opt-out. |
| Registro de opt-out | Indefinido | Para evitar reenvíos si volvés a escanear. Es a tu favor. |
| Logs técnicos | 12 meses | Rotación automática mensual. |
| Registros de pago | 6 años | Obligación legal SII. |

## 10. Fuente de los datos

Todos los datos personales que tratamos provienen **directamente del titular** al momento de registrarse, escanear su sitio o contratar el Módulo Pro. **No utilizamos fuentes de acceso público** ni adquirimos datos de terceros.

## 11. Derecho a retirar el consentimiento

Cuando el tratamiento se base en tu consentimiento, tienes derecho a retirarlo en cualquier momento sin que ello afecte la licitud del tratamiento basado en el consentimiento previo a su retirada. Para retirarlo, escribe a privacidad@leydedatoschile.com o usa "Eliminar mi cuenta" en /cuenta.

## 12. Decisiones automatizadas y elaboración de perfiles

El servicio utiliza **modelos de lenguaje (LLMs)** para analizar el HTML público de sitios web y generar reportes de cumplimiento. Esta operación constituye una decisión automatizada sobre el cumplimiento de tu sitio.

- **Lógica aplicada:** el LLM recibe señales técnicas extraídas del HTML (HTTPS, banner de cookies, formularios, scripts de terceros) y emite un score entre 0 y 100 más un listado de hallazgos basado en la Ley 21.719.
- **Consecuencias previstas:** el score es solo diagnóstico orientativo. No se comparte con terceros ni se usa para decisiones contractuales sin tu intervención.
- **Derecho a oposición:** puedes oponerte a este tratamiento solicitando análisis manual escribiendo a privacidad@leydedatoschile.com.

## 13. Cookies y tecnologías similares

Usamos dos tipos de cookies/almacenamiento:

- **Necesarias** (siempre activas): sesión, autenticación JWT, registro de tu propio consentimiento. Son de primera parte y no salen de nuestro servidor (Chile).
- **Analíticas opcionales** (solo con tu consentimiento explícito): usamos **PostHog** (PostHog Inc., EE.UU.) para métricas de uso y session recording de interacciones, y **Google Analytics 4** (Google LLC, EE.UU.) para medir el embudo de conversión con IP anonimizada. **El contenido que escribes en formularios se enmascara automáticamente** y nunca llega a PostHog. Esta categoría está **desactivada por defecto** y solo se carga si la aceptás en el banner de cookies.

**No usamos** cookies de marketing, retargeting, Meta Pixel, TikTok Pixel ni similares.

Puedes cambiar tus preferencias en cualquier momento desde el botón **"Preferencias de cookies"** del footer. Ver detalle completo en la [Política de Cookies](/politica-cookies).

## 14. Comunicaciones por email (transaccionales + educativas opcionales)

Distinguimos dos tipos de email **claramente separados** y con bases legales distintas:

**14.1 Emails transaccionales (siempre que aplique).** Recibo del scan, recuperación de contraseña, recibo de pago Pro, aviso de expiración de tu acceso Pro, notificaciones críticas de cuenta. Base legal: **ejecución del contrato** + obligación legal. No requieren consentimiento de marketing.

**14.2 Emails educativos opcionales (drip de 3 emails).** Si al desbloquear tu reporte de scan aceptás expresamente recibirlos, te enviamos **exactamente 3 emails** con contenido educativo sobre la Ley 21.719:

- **Email 1** (~24 horas tras el scan): "Ley 21.719 en 3 minutos — qué cambia para tu PYME".
- **Email 2** (~3 días): "Caso real 2025 — multas, amonestaciones y cómo evitarlos".
- **Email 3** (~7 días): "Último mensaje — 3 escenarios a 12 meses".

Características del programa:

- **Base legal:** consentimiento explícito y específico (Art. 12 de la Ley 21.719), separado del consentimiento de tratamiento general.
- **Finitos:** son 3 emails y termina. No te suscribimos a un newsletter perpetuo.
- **Cancelación con un clic:** cada email incluye un link de baja en el footer y soportamos el header **List-Unsubscribe** (RFC 8058 One-Click Unsubscribe), así que tu cliente de email puede darte de baja sin abrir nuestro sitio.
- **Cancelación automática:** si compras el Módulo Pro entre el scan y el final del drip, **detenemos automáticamente los siguientes emails** — ya convertiste, ya no tiene sentido seguir educándote.
- **Opt-out permanente:** una vez que te das de baja, **no volveremos a programar este drip aunque vuelvas a escanear**, salvo que vos explícitamente lo reactives.
- **Sin terceros:** los emails se envían via [Resend](https://resend.com) (encargado del tratamiento, EE.UU., cláusulas Min. Economía). No compartimos tu email con red publicitaria, broker ni similar.

**Cómo darte de baja:** click en "Darme de baja" en el pie de cualquier email, o escribí a *privacidad@leydedatoschile.com* con asunto "Baja drip educativo".

## Contacto

- **Email:** privacidad@leydedatoschile.com
- **Asunto:** Privacidad Ley de Datos Chile

---

*Última actualización: 22 de febrero de 2026 · Versión 3.1*

*Esta política está redactada cumpliendo los 12 requisitos del Art. 14 ter de la Ley N° 21.719 y se mantiene como ejemplo aplicado del cumplimiento que ofrecemos a nuestros clientes.*`;

export default function PrivacyPolicy() {
  return (
    <>
      <SEO
        title="Política de Privacidad — Cumplimiento Art. 14 ter Ley 21.719"
        description="Política de Privacidad de Ley de Datos Chile cubriendo los 12 requisitos del Art. 14 ter de la Ley N° 21.719: responsable, finalidades, derechos ARCO, decisiones automatizadas, transferencias internacionales con cláusulas Min. Economía."
        path="/politica-privacidad"
        keywords="política de privacidad Art 14 ter Ley 21.719, derechos ARCO, decisiones automatizadas, transferencias internacionales Min Economía Chile"
        schemas={[
          orgSchema,
          breadcrumbSchema([
            { name: "Inicio", path: "/" },
            { name: "Política de Privacidad", path: "/politica-privacidad" },
          ]),
        ]}
      />
      <section className="px-6 py-12 sm:py-16">
        <div className="max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1E3A8A]/5 text-[#1E3A8A] text-xs font-semibold uppercase tracking-wide mb-6">
            <ShieldCheck className="w-3.5 h-3.5" />
            Política de Privacidad
          </div>
          <h1
            data-testid="privacy-title"
            className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-900"
          >
            Cómo tratamos tus datos personales
          </h1>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            Este documento es nuestra propia Política de Privacidad y constituye un ejemplo aplicado de lo que la Ley N° 21.719 exige. Si vas a usar Ley de Datos Chile, lee esta página y, si tienes dudas, escríbenos.
          </p>

          <article
            data-testid="privacy-content"
            className="mt-10 prose prose-slate max-w-none prose-headings:font-display prose-headings:tracking-tight prose-h2:text-xl prose-h2:mt-10 prose-h2:mb-3 prose-p:leading-relaxed prose-li:my-1 prose-strong:text-slate-900 prose-a:text-[#1E3A8A]"
          >
            <ReactMarkdown>{PRIVACY_POLICY}</ReactMarkdown>
          </article>

          <div className="mt-14 p-7 rounded-2xl bg-slate-50 border border-slate-200">
            <h3 className="font-display text-xl font-semibold text-slate-900">
              ¿Tu PYME necesita su propia Política de Privacidad?
            </h3>
            <p className="mt-2 text-slate-600">
              Genera una conforme a la Ley 21.719 en menos de 10 minutos, adaptada a tu negocio.
            </p>
            <Link
              to="/modulo-pro"
              className="mt-5 inline-flex items-center gap-2 h-12 px-6 rounded-lg bg-[#1E3A8A] hover:bg-[#1E40AF] text-white font-semibold transition-colors"
            >
              Generar mi Política de Privacidad
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
